import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Card, CardStatus } from "@feed/shared";
import { api } from "../api";
import { CardBlock } from "../components/CardBlock";
import { DetailSheet } from "../components/DetailSheet";
import { Screen } from "../components/Screen";
import { useToast } from "../components/Toast";
import { useDocumentTitle } from "../lib/title";
import { useCardList } from "../lib/useCardList";
import { useInfiniteScroll } from "../lib/useInfiniteScroll";
import { useStatusChange } from "../lib/useStatusChange";

type Filter = "unread" | "read" | "all";
const FILTERS: Filter[] = ["unread", "read", "all"];
const TYPE_POLL_MS = 60_000;

export function Feed() {
  const { t } = useTranslation();
  useDocumentTitle(t("feed.title"));
  const toast = useToast();

  const [type, setType] = useState<string>("");
  const [filter, setFilter] = useState<Filter>("unread");
  const [types, setTypes] = useState<{ type: string; unread: number }[]>([]);
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const status: CardStatus | "all" = filter;
  const list = useCardList({ status, type: type || undefined, poll: true });

  const refreshTypes = useCallback(() => {
    api
      .types()
      .then(({ types: found }) => {
        setTypes(found);
        // A tab whose cards all left the feed (dismissed) is no longer offered.
        setType((current) => (current && !found.some((entry) => entry.type === current) ? "" : current));
      })
      .catch(() => undefined);
  }, []);

  useEffect(() => {
    refreshTypes();
    const tick = () => document.visibilityState === "visible" && refreshTypes();
    const id = window.setInterval(tick, TYPE_POLL_MS);
    document.addEventListener("visibilitychange", tick);
    return () => {
      window.clearInterval(id);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [refreshTypes]);

  const change = useStatusChange(list, refreshTypes);
  const totalUnread = types.reduce((sum, entry) => sum + entry.unread, 0);

  // What the current filter shows. The card whose sheet is open stays put, so it doesn't
  // vanish from under the sheet the moment opening it marks it read.
  const matches = (card: Card) => card.id === openId || filter === "all" || card.status === filter;
  const shown = list.cards.filter(matches);
  const open = list.cards.find((card) => card.id === openId) ?? null;

  function openCard(card: Card) {
    setOpenId(card.id);
    if (card.status === "unread") void change(card, "read", { quiet: true });
  }

  // Marks every unread card of the current tab, not just the ones scrolled into view.
  async function markAllRead() {
    setBusy(true);
    try {
      const ids: string[] = [];
      let before: string | undefined;
      do {
        const page = await api.cards.list({ status: "unread", type: type || undefined, limit: 100, before });
        ids.push(...page.cards.map((card) => card.id));
        before = page.next_cursor ?? undefined;
      } while (before);
      for (let i = 0; i < ids.length; i += 200) await api.cards.setStatusMany(ids.slice(i, i + 200), "read");
      if (ids.length > 0) toast.show(t("toast.allRead", { count: ids.length }));
      list.reload();
      refreshTypes();
    } catch {
      toast.show(t("errors.save"));
    } finally {
      setBusy(false);
    }
  }

  // Infinite scroll: a sentinel under the last card asks for the next page when it
  // comes into view.
  const sentinel = useInfiniteScroll(list.hasMore, list.loadMore, shown.length);

  return (
    <Screen>
      <header className="page-head">
        <div>
          <h1 className="title" tabIndex={-1}>{t("feed.title")}</h1>
          <p className="subtitle">{t("feed.unread", { count: totalUnread })}</p>
        </div>
        <button type="button" className="link-button" onClick={() => void markAllRead()} disabled={busy || filter === "read"}>
          {t("feed.markAllRead")}
        </button>
      </header>

      <div className="chips chips-scroll" role="group" aria-label={t("feed.tabs")}>
        <button type="button" className="chip" aria-pressed={type === ""} onClick={() => setType("")}>
          {t("feed.all")}
        </button>
        {types.map((entry) => (
          <button key={entry.type} type="button" className="chip" aria-pressed={type === entry.type} onClick={() => setType(entry.type)}>
            <bdi>{entry.type}</bdi>
            {entry.unread > 0 && <span className="chip-count">{entry.unread}</span>}
          </button>
        ))}
      </div>

      <div className="segmented" role="group" aria-label={t("feed.filter")}>
        {FILTERS.map((candidate) => (
          <button key={candidate} type="button" className="segmented-option" aria-pressed={filter === candidate} onClick={() => setFilter(candidate)}>
            {t(`feed.filters.${candidate}`)}
          </button>
        ))}
      </div>

      {list.pending.length > 0 && (
        <button type="button" className="new-pill" onClick={() => { list.showPending(); window.scrollTo({ top: 0, behavior: "smooth" }); }}>
          {list.pending.length === 1 ? t("feed.newCard") : t("feed.newCards", { count: list.pending.length })}
        </button>
      )}

      {list.error && (
        <div className="notice" role="alert">
          <span>{t(`errors.${list.error}`, { defaultValue: t("errors.load") })}</span>
          <button type="button" className="link-button" onClick={list.reload}>
            {t("common.retry")}
          </button>
        </div>
      )}
      {list.loading && <p className="status">{t("loading")}</p>}
      {!list.loading && !list.error && shown.length === 0 && <p className="status">{filter === "unread" ? t("feed.emptyUnread") : t("feed.emptyOther")}</p>}

      <div className="stack">
        {shown.map((card) => (
          <CardBlock key={card.id} card={card} onOpen={() => openCard(card)} />
        ))}
      </div>
      {list.hasMore && <div ref={sentinel} className="sentinel" aria-hidden="true" />}
      {list.loadingMore && <p className="status">{t("loading")}</p>}

      {open && (
        <DetailSheet
          card={open}
          onClose={() => setOpenId(null)}
          onSetStatus={(next) => {
            setOpenId(null);
            void change(open, next);
          }}
        />
      )}
    </Screen>
  );
}
